import { useEffect, useMemo, useState } from 'react'
import { Alert, Select, Stack, Text, type ComboboxItem } from '@mantine/core'
import { DatePickerInput, type DatesRangeValue } from '@mantine/dates'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { TbAlertTriangle } from 'react-icons/tb'
import { getDetectionComparisonFirstDrivesOptions, getDetectionComparisonSecondDrivesOptions } from '@/api/main'
import type { ComparableDriveModel } from '@/api/main/types.gen'
import useDebounce from '@/hooks/useDebounceSearch'
import { formatDateTime } from '@/utils/datetime'
import { errorMessage } from '@/utils/other'

const SEARCH_DEBOUNCE_MS = 300
const PAGE_LIMIT = 50

type Props = {
  firstDriveId: number | undefined
  secondDriveId: number | undefined
  onChange: (firstDriveId: number | undefined, secondDriveId: number | undefined) => void
}

type DriveLabel = (drive: ComparableDriveModel) => string

/** Label shown for a selected drive id that is not part of the loaded page. */
const fallbackLabel = (id: number) => `#${id}`

const toOptions = (
  drives: ComparableDriveModel[] | undefined,
  selectedId: number | undefined,
  selectedDrive: ComparableDriveModel | null,
  label: DriveLabel
): ComboboxItem[] => {
  const options = (drives ?? []).map(drive => ({ value: String(drive.scheduled_drive_id), label: label(drive) }))
  // Keep the current value visible even when it is outside the current search page.
  if (selectedId !== undefined && !options.some(option => option.value === String(selectedId))) {
    options.unshift({
      value: String(selectedId),
      label: selectedDrive === null ? fallbackLabel(selectedId) : label(selectedDrive),
    })
  }
  return options
}

/**
 * Mantine echoes the selected option's label into a controlled search value, which must
 * not be sent to the backend as a drive id search. The echoed label is either the full
 * label of the known drive or the fallback label used before the drive is known.
 */
const apiSearch = (
  search: string,
  selectedId: number | undefined,
  selectedDrive: ComparableDriveModel | null,
  label: DriveLabel
): string | undefined => {
  const trimmed = search.trim()
  if (trimmed === '') return undefined
  if (selectedId !== undefined && trimmed === fallbackLabel(selectedId)) return undefined
  if (selectedDrive !== null && trimmed === label(selectedDrive)) return undefined
  return trimmed
}

const parseId = (value: string | null): number | undefined => (value === null ? undefined : Number(value))

const findDrive = (drives: ComparableDriveModel[] | undefined, id: number | undefined) =>
  id === undefined ? null : (drives?.find(drive => drive.scheduled_drive_id === id) ?? null)

export function DriveSelectors({ firstDriveId, secondDriveId, onChange }: Props) {
  const { t } = useTranslation('detectionComparison')
  const [dateRange, setDateRange] = useState<DatesRangeValue<string>>([null, null])
  const [firstSearch, setFirstSearch] = useState('')
  const [secondSearch, setSecondSearch] = useState('')
  // The selected drives are remembered so their labels survive searches that page them out.
  const [firstDrive, setFirstDrive] = useState<ComparableDriveModel | null>(null)
  const [secondDrive, setSecondDrive] = useState<ComparableDriveModel | null>(null)

  const label = useMemo<DriveLabel>(
    () => drive => {
      const base = t('drives.option', {
        id: drive.scheduled_drive_id,
        vehicle: drive.vehicle_id,
        route: drive.route_id,
        date: formatDateTime(drive.start_after),
      })
      return drive.drive_started_at ? base : `${base} · ${t('drives.notStarted')}`
    },
    [t]
  )

  const debouncedFirstSearch = useDebounce(apiSearch(firstSearch, firstDriveId, firstDrive, label), SEARCH_DEBOUNCE_MS)
  const debouncedSecondSearch = useDebounce(
    apiSearch(secondSearch, secondDriveId, secondDrive, label),
    SEARCH_DEBOUNCE_MS
  )

  const dateQuery = { date_from: dateRange[0] ?? undefined, date_to: dateRange[1] ?? undefined }

  const firstDrives = useQuery(
    getDetectionComparisonFirstDrivesOptions({
      query: { ...dateQuery, search: debouncedFirstSearch, limit: PAGE_LIMIT },
    })
  )
  const secondDrives = useQuery({
    ...getDetectionComparisonSecondDrivesOptions({
      query: { ...dateQuery, first_drive_id: firstDriveId ?? 0, search: debouncedSecondSearch, limit: PAGE_LIMIT },
    }),
    enabled: firstDriveId !== undefined,
  })

  // Drives preselected via the URL become known once a loaded page contains them.
  useEffect(() => {
    const found = findDrive(firstDrives.data?.drives, firstDriveId)
    if (found !== null) setFirstDrive(found)
  }, [firstDrives.data, firstDriveId])
  useEffect(() => {
    const found = findDrive(secondDrives.data?.drives, secondDriveId)
    if (found !== null) setSecondDrive(found)
  }, [secondDrives.data, secondDriveId])

  const firstOptions = useMemo(
    () => toOptions(firstDrives.data?.drives, firstDriveId, firstDrive, label),
    [firstDrives.data, firstDriveId, firstDrive, label]
  )
  const secondOptions = useMemo(
    () => toOptions(secondDrives.data?.drives, secondDriveId, secondDrive, label),
    [secondDrives.data, secondDriveId, secondDrive, label]
  )

  const listError = firstDrives.isError ? firstDrives.error : secondDrives.isError ? secondDrives.error : null
  const firstRoute =
    firstDrive !== null && firstDrive.scheduled_drive_id === firstDriveId ? firstDrive.route_id : undefined

  return (
    <Stack gap="sm">
      <Text fw={600}>{t('drives.section')}</Text>
      {listError !== null && (
        <Alert color="red" icon={<TbAlertTriangle />}>
          {errorMessage(listError, t('drives.failedToLoad'))}
        </Alert>
      )}
      <DatePickerInput
        type="range"
        allowSingleDateInRange
        clearable
        label={t('drives.dateRange')}
        value={dateRange}
        onChange={setDateRange}
      />
      <Select
        label={t('drives.first')}
        placeholder={t('drives.searchPlaceholder')}
        data={firstOptions}
        value={firstDriveId === undefined ? null : String(firstDriveId)}
        onChange={value => {
          const id = parseId(value)
          setFirstDrive(findDrive(firstDrives.data?.drives, id))
          setSecondDrive(null)
          onChange(id, undefined)
        }}
        searchable
        clearable
        searchValue={firstSearch}
        onSearchChange={setFirstSearch}
        filter={({ options }) => options}
        nothingFoundMessage={t('drives.noDrives')}
      />
      <Select
        label={t('drives.second')}
        description={
          firstDriveId === undefined
            ? undefined
            : firstRoute === undefined
              ? t('drives.sameRouteHint')
              : t('drives.routeHint', { route: firstRoute })
        }
        placeholder={firstDriveId === undefined ? t('drives.selectFirst') : t('drives.searchPlaceholder')}
        disabled={firstDriveId === undefined}
        data={secondOptions}
        value={secondDriveId === undefined ? null : String(secondDriveId)}
        onChange={value => {
          const id = parseId(value)
          setSecondDrive(findDrive(secondDrives.data?.drives, id))
          onChange(firstDriveId, id)
        }}
        searchable
        clearable
        searchValue={secondSearch}
        onSearchChange={setSecondSearch}
        filter={({ options }) => options}
        nothingFoundMessage={t('drives.noDrives')}
      />
    </Stack>
  )
}
